/**
 * Compatibility adapters for React Query hooks
 * These hooks wrap React Query and maintain backward compatibility with existing component interfaces
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useUserVideosWithPinned } from '../dashboard/useVideosQuery';
import { useUserImagesWithMutations } from '../dashboard/useImagesQuery';
import type { UserVideo } from '@/api/videoApi';
import type { UserImage } from '@/api/imageApi';

/**
 * Adapter for useUserVideos that maintains backward compatibility
 * Wraps React Query hooks but provides the same interface as the old manual hook
 */
interface UseUserVideosAdapterParams {
  userEmail: string | null;
  isAdmin?: boolean;
  limit?: number;
  status?: string;
  sort_by?: string;
  enabled?: boolean;
}

export const useUserVideosAdapter = (params: UseUserVideosAdapterParams) => {
  const queryResult = useUserVideosWithPinned({
    userEmail: params.userEmail,
    limit: params.limit,
    status: params.status,
    sort_by: params.sort_by,
    enabled: params.enabled,
  });

  // Local state for optimistic updates (enables smooth transitions)
  const [localVideos, setLocalVideos] = useState<UserVideo[]>([]);

  // Merge optimistic videos with React Query data
  // React Query data takes precedence (has thumbnails), optimistic videos only shown if not in React Query yet
  const videos = useMemo(() => {
    const optimisticOnly = localVideos.filter(
      local => !queryResult.videos.some(query => query.id === local.id)
    );
    return [...optimisticOnly, ...queryResult.videos];
  }, [localVideos, queryResult.videos]);

  const pinnedVideos = queryResult.pinnedVideos;

  // Clean up local state - remove items that are now in React Query
  // This prevents duplicates and ensures deletions work properly
  useEffect(() => {
    if (localVideos.length === 0) return;
    
    setLocalVideos(prev => {
      const queryIds = new Set(queryResult.videos.map(v => v.id));
      // Keep only truly optimistic items (not yet synced to React Query)
      // Remove items that have been synced to query (they'll be shown from query instead)
      const filtered = prev.filter(local => queryIds.has(local.id) === false);
      
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [queryResult.videos, localVideos.length]);

  // Setter for videos that supports optimistic updates
  const setVideosWrapper = useCallback((updater: UserVideo[] | ((prev: UserVideo[]) => UserVideo[])) => {
    setLocalVideos(prev => {
      const current = typeof updater === 'function' ? updater(prev) : updater;
      return current;
    });
  }, []);

  // No-op for pinned videos (React Query manages these automatically)
  const setPinnedVideosWrapper = useCallback((..._args: unknown[]) => {}, []);

  // Download last frame handler
  const handleDownloadLastFrame = useCallback(async (signedUrl: string) => {
    // Import dynamically to avoid circular dependencies
    const { downloadLastFrameFromUrl } = await import('@/utils/common/videoUtils');
    return downloadLastFrameFromUrl(signedUrl);
  }, []);

  // Allowed to pin state - automatically synced with pinned videos
  // User can pin if they haven't pinned any video themselves (max 1 video pinned BY USER)
  // Note: Admin can pin multiple videos, so we only count videos pinned by THIS user
  const isAllowedToPin = useMemo(() => {
    if (!params.userEmail) return false;

    // Count only videos pinned BY this user (not by admin)
    const userPinnedCount = pinnedVideos.filter(
      (video: UserVideo) => video.pinned_by === params.userEmail
    ).length;

    return userPinnedCount === 0;
  }, [pinnedVideos, params.userEmail]);

  // Legacy compatibility function (no longer needed but kept for backward compatibility)
  const fetchAllowedToPin = useCallback(async () => {
    // This is now a no-op since isAllowedToPin is automatically computed
    // Kept for backward compatibility with existing code
  }, []);

  // Optimistic pin/unpin functions - these now call actual React Query mutations
  // Empty deps to avoid infinite loops - closures will capture latest values
  const optimisticPinVideo = useCallback((videoId: string, userEmail: string) => {
    // Trigger React Query mutation which handles API calls + cache updates
    // The cache update provides immediate UI feedback
    queryResult.pinVideo(videoId, userEmail);

    // Note: isAllowedToPin will automatically update when pinnedVideos changes
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const optimisticUnpinVideo = useCallback((videoId: string) => {
    // Trigger React Query mutation - need userEmail from params
    if (params.userEmail) {
      queryResult.unpinVideo(videoId, params.userEmail);
    }

    // Note: isAllowedToPin will automatically update when pinnedVideos changes
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable loadMore callback
  const handleLoadMore = useCallback(async () => {
    await queryResult.loadMore();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Predictive prefetch - same as loadMore but doesn't block UI
  const handlePredictivePrefetch = useCallback(async () => {
    // Silently prefetch next page data without triggering loading states
    // React Query will cache this for instant display when user scrolls
    if (queryResult.hasMore && !queryResult.isLoadingMore) {
      await queryResult.loadMore();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => ({
    // Data
    videos,
    pinnedVideos,
    setVideos: setVideosWrapper,
    setPinnedVideos: setPinnedVideosWrapper,

    // States
    error: queryResult.error ? String(queryResult.error) : null,
    isLoading: queryResult.isLoading,
    isLoadingMore: queryResult.isLoadingMore,
    hasMore: queryResult.hasMore,

    // Counts
    totalCount: queryResult.totalCount,
    totalUnfilteredCount: queryResult.totalUnfilteredCount,

    // Functions
    refetch: queryResult.refetch,
    loadMore: handleLoadMore,
    predictivePrefetch: handlePredictivePrefetch,
    handleDownloadLastFrame,

    // Optimistic updates
    optimisticPinVideo,
    optimisticUnpinVideo,

    // Pin permissions
    isAllowedToPin,
    fetchAllowedToPin,
  }), [
    videos,
    pinnedVideos,
    setVideosWrapper,
    setPinnedVideosWrapper,
    queryResult.error,
    queryResult.isLoading,
    queryResult.isLoadingMore,
    queryResult.hasMore,
    queryResult.totalCount,
    queryResult.totalUnfilteredCount,
    queryResult.refetch,
    handleLoadMore,
    handlePredictivePrefetch,
    handleDownloadLastFrame,
    optimisticPinVideo,
    optimisticUnpinVideo,
    isAllowedToPin,
    fetchAllowedToPin,
  ]);
};

/**
 * Adapter for useUserImages that maintains backward compatibility
 */
interface UseUserImagesAdapterParams {
  userEmail: string | null;
  limit?: number;
  status?: string;
  sort_by?: string;
  mode?: string;
  enabled?: boolean;
}

export const useUserImagesAdapter = (params: UseUserImagesAdapterParams) => {
  const queryResult = useUserImagesWithMutations({
    userEmail: params.userEmail,
    limit: params.limit,
    status: params.status,
    sort_by: params.sort_by,
    mode: params.mode,
    enabled: params.enabled,
  });

  // Local state for images (for compatibility with optimistic updates)
  const [localImages, setLocalImages] = useState<UserImage[]>([]);

  // Merge optimistic images with React Query data
  // React Query data takes precedence, optimistic images only shown if not in React Query yet
  const images = useMemo(() => {
    const optimisticOnly = localImages.filter(
      local => !queryResult.images.some(query => query.id === local.id)
    );
    return [...optimisticOnly, ...queryResult.images];
  }, [localImages, queryResult.images]);

  const setImagesWrapper = useCallback((updater: UserImage[] | ((prev: UserImage[]) => UserImage[])) => {
    setLocalImages(prev => {
      const current = typeof updater === 'function' ? updater(prev) : updater;
      return current;
    });
  }, []);

  // Clean up local state - remove items that are now in React Query
  // This prevents duplicates and ensures deletions work properly
  useEffect(() => {
    if (localImages.length === 0) return;
    
    setLocalImages(prev => {
      const queryIds = new Set(queryResult.images.map(img => img.id));
      // Keep only truly optimistic items (not yet synced to React Query)
      // Remove items that have been synced to query (they'll be shown from query instead)
      const filtered = prev.filter(local => queryIds.has(local.id) === false);
      
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [queryResult.images, localImages.length]);

  // Stable loadMore callback
  const handleLoadMore = useCallback(async () => {
    await queryResult.loadMore();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Predictive prefetch for images
  const handlePredictivePrefetch = useCallback(async () => {
    if (queryResult.hasMore && !queryResult.isLoadingMore) {
      await queryResult.loadMore();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => ({
    // Data
    images,
    setImages: setImagesWrapper,

    // States
    error: queryResult.error ? String(queryResult.error) : null,
    isLoading: queryResult.isLoading,
    isLoadingMore: queryResult.isLoadingMore,
    hasMore: queryResult.hasMore,

    // Counts
    totalCount: queryResult.totalCount,
    totalUnfilteredCount: queryResult.totalUnfilteredCount,

    // Functions
    refetch: queryResult.refetch,
    loadMore: handleLoadMore,
    predictivePrefetch: handlePredictivePrefetch,
    deleteImage: queryResult.deleteImage,
  }), [
    images,
    setImagesWrapper,
    queryResult.error,
    queryResult.isLoading,
    queryResult.isLoadingMore,
    queryResult.hasMore,
    queryResult.totalCount,
    queryResult.totalUnfilteredCount,
    queryResult.refetch,
    handleLoadMore,
    handlePredictivePrefetch,
    queryResult.deleteImage,
  ]);
};
